// src/components/SensorPage/TechnicalData.jsx - Design Ultra Premium
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiClock, 
  FiPlay, 
  FiPause,
  FiSliders,
  FiActivity,
  FiTrendingUp,
  FiTrendingDown,
  FiZap,
  FiAlertCircle,
  FiCheckCircle,
  FiBarChart2,
  FiCalendar,
  FiSun,
  FiMoon,
  FiThermometer,
  FiDroplet,
  FiWind,
  FiSettings,
  FiRefreshCw,
  FiTarget  // ADICIONE ESTA LINHA
} from 'react-icons/fi';
import { 
  AreaChart, 
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  PieChart,
  Pie,
  Cell,
  Scatter
} from 'recharts';
import CountUp from 'react-countup';
import './SensorPageSections.css';
import './TechnicalData.css'; // ← IMPORTANTE: Criar arquivo CSS separado

const TechnicalData = ({ data, activeSubsection }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [animateCharts, setAnimateCharts] = useState(false);
  const { technical, current, historical } = data;

  useEffect(() => {
    setAnimateCharts(true);
    const timer = setTimeout(() => setAnimateCharts(false), 1500);
    return () => clearTimeout(timer);
  }, [activeSubsection]);

  // Componente wrapper seguro para CountUp
  const SafeCountUp = ({ value, suffix = '', decimals = 2, duration = 2, className = '', start = 0 }) => {
    const [isMounted, setIsMounted] = useState(false);
    const containerRef = useRef(null);
    
    useEffect(() => {
      setIsMounted(true);
    }, []);
    
    // Verificar se value é válido
    const isValidValue = (val) => {
      return val !== null && 
             val !== undefined && 
             typeof val === 'number' &&
             !isNaN(val) && 
             isFinite(val);
    };
    
    if (!isValidValue(value)) {
      return (
        <span className={className || 'runtime-value'}>
          N/A{suffix}
        </span>
      );
    }
    
    const safeValue = Math.max(0, value);
    
    if (!isMounted) {
      return (
        <span className={className || 'runtime-value'}>
          {safeValue.toFixed(decimals)}{suffix}
        </span>
      );
    }
    
    return (
      <div ref={containerRef}>
        <CountUp
          start={start}
          end={safeValue}
          duration={duration}
          decimals={decimals}
          suffix={suffix}
          className={className || 'runtime-value'}
        />
      </div>
    );
  };

  // Componente para Tempo HVAC
  const RuntimeView = () => {
    const isActive = technical.hvacRuntime.isActive;
    const activeHours = technical.hvacRuntime.last24h.active;
    const inactiveHours = technical.hvacRuntime.last24h.inactive;
    const efficiency = ((activeHours / 24) * 100).toFixed(2);

    // Dados para o gráfico de distribuição horária
    const hourlyData = Array.from({ length: 24 }, (_, i) => {
      const hour = i;
      const isActiveHour = (hour >= 7 && hour <= 20) ? Math.random() > 0.3 : Math.random() > 0.8;
      return {
        hour: `${hour.toString().padStart(2, '0')}h`,
        status: isActiveHour ? 1 : 0,
        temperatura: 22 + Math.sin(hour * 0.3) * 3,
        eficiencia: isActiveHour ? 3.5 + Math.random() * 0.5 : 0
      };
    });

    // Dados para o gráfico circular
    const circularData = [
      { name: 'Ativo', value: activeHours, fill: '#3b82f6' },
      { name: 'Inativo', value: inactiveHours, fill: '#e5e7eb' }
    ];

    // Estatísticas de ciclos
    const cycleStats = {
      total: 8,
      morning: 3,
      afternoon: 3,
      evening: 2,
      avgDuration: '1h 45min'
    };

    return (
      <div className="technical-view-container">
        {/* Header Premium */}
        <div className="technical-header-premium runtime-gradient">
          <div className="header-glow"></div>
          <div className="header-content-tech">
            <div className="header-left-tech">
            
<div className="header-icon-tech">
  <motion.div 
    animate={{ rotate: isActive ? 360 : 0 }}
    transition={{ duration: 2, repeat: isActive ? Infinity : 0, ease: "linear" }}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <FiClock size={40} />
  </motion.div>
              </div>

              <div>
                <h1 className="header-title-tech">Tempo de Funcionamento HVAC</h1>
                <p className="header-subtitle-tech">
                  Monitoramento do tempo de atividade do sistema de climatização nas últimas 24 horas
                </p>
              </div>
            </div>
            <div className="header-right-tech">
              <div className="runtime-display">
                <SafeCountUp 
                  value={activeHours}
                  suffix="h"
                  duration={2}
                  className="runtime-value"
                />
                <span className="runtime-label">ativas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Status Cards Melhorados */}
        <div className="status-cards-grid">
          {/* Card de Status */}
          <motion.div 
            className={`status-card-tech ${isActive ? 'active' : 'inactive'}`}
            whileHover={{ scale: 1.02 }}
          >
            <div className="status-indicator">
              <div className="status-header-row">
                <span className="status-label-small">STATUS ATUAL</span>
                <div className="status-icon-animated">
                  {isActive ? <FiPlay size={24} /> : <FiPause size={24} />}
                </div>
              </div>
              
              <div className="status-main">
                <div className={`status-light ${isActive ? 'on' : 'off'}`}></div>
                <div className="status-info">
                  <div className="status-text">
                    {isActive ? 'ATIVO' : 'INATIVO'}
                  </div>
                  <div className="status-time-badge">
                    <FiClock size={14} />
                    <span>{isActive ? 'Ligado há 2h 15min' : 'Desligado há 45min'}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card de Eficiência */}
          <motion.div 
            className="status-card-tech efficiency-card"
            whileHover={{ scale: 1.02 }}
          >
            <div className="efficiency-meter">
              <div className="efficiency-gauge-wrapper">
                <svg className="circular-gauge" viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3b82f6" />
                      <stop offset="100%" stopColor="#06b6d4" />
                    </linearGradient>
                  </defs>
                  <circle
                    className="gauge-background-circle"
                    cx="50"
                    cy="50"
                    r="40"
                  />
                  <circle
                    className="gauge-progress-circle"
                    cx="50"
                    cy="50"
                    r="40"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - efficiency / 100)}`}
                  />
                </svg>
                <div className="gauge-center-text">
                  <div className="gauge-value-text">{efficiency}</div>
                  <div className="gauge-value-suffix">%</div>
                </div>
              </div>
              <div className="efficiency-label-premium">EFICIÊNCIA DE USO</div>
            </div>
          </motion.div>

          {/* Card de Ciclos */}
          <motion.div 
            className="status-card-tech cycles-card"
            whileHover={{ scale: 1.02 }}
          >
            <div className="cycles-info">
              <div className="cycle-header">
                <div className="cycle-header-left">
                  <div className="cycle-icon-wrapper">
                    <FiRefreshCw size={20} />
                  </div>
                  <span className="cycle-label">CICLOS ON/OFF</span>
                </div>
              </div>
              
              <div className="cycle-value-large">{cycleStats.total}</div>
              
              <div className="cycle-details-grid">
                <div className="cycle-detail-item">
                  <div className="cycle-detail-icon">
                    <FiSun size={14} />
                    <span>Manhã</span>
                  </div>
                  <div className="cycle-detail-value">{cycleStats.morning}</div>
                </div>
                <div className="cycle-detail-item">
                  <div className="cycle-detail-icon">
                    <FiSun size={14} />
                    <span>Tarde</span>
                  </div>
                  <div className="cycle-detail-value">{cycleStats.afternoon}</div>
                </div>
                <div className="cycle-detail-item">
                  <div className="cycle-detail-icon">
                    <FiMoon size={14} />
                    <span>Noite</span>
            </div>
                  <div className="cycle-detail-value">{cycleStats.evening}</div>
              </div>
            </div>
            </div>
          </motion.div>
          </div>

        {/* Gráfico de Distribuição 24h */}
        <div className="chart-section-tech">
          <div className="chart-header-tech">
              <h3>Distribuição 24h</h3>
            <div className="chart-legend-tech">
              <span className="legend-item-tech">
                <span className="legend-dot" style={{ background: '#3b82f6' }}></span>
                Ativo
              </span>
              <span className="legend-item-tech">
                <span className="legend-dot" style={{ background: '#e5e7eb' }}></span>
                Inativo
              </span>
            </div>
            </div>
          
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={hourlyData}>
              <defs>
                <linearGradient id="activeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis 
                dataKey="hour" 
                stroke="#9ca3af"
                fontSize={11}
                interval={2}
              />
              <YAxis 
                yAxisId="left"
                stroke="#9ca3af"
                fontSize={11}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#9ca3af"
                fontSize={11}
              />
                <Tooltip 
                  contentStyle={{ 
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Bar 
                yAxisId="left"
                dataKey="status" 
                fill="url(#activeGradient)"
                radius={[4, 4, 0, 0]}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="temperatura" 
                stroke="#ef4444" 
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
            </ResponsiveContainer>
        </div>

        {/* Análise de Padrões */}
        <div className="patterns-section">
          <h3 className="section-title-tech">Análise de Padrões</h3>
          <div className="pattern-cards">
            <div className="pattern-card">
              <FiCalendar className="pattern-icon" />
              <div className="pattern-info">
                <h4>Padrão Semanal</h4>
                <p>Maior uso entre Segunda e Sexta</p>
                <div className="pattern-bar">
                  <div className="bar-fill" style={{ width: '85%' }}></div>
                </div>
              </div>
            </div>
            <div className="pattern-card">
              <FiActivity className="pattern-icon" />
              <div className="pattern-info">
                <h4>Pico de Uso</h4>
                <p>14h às 16h (maior demanda)</p>
                <div className="pattern-bar">
                  <div className="bar-fill" style={{ width: '92%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Componente para Optimum Start/Stop
  const OptimusView = () => {
    const optimalStart = technical.hvacRuntime.optimal.start;
    const optimalStop = technical.hvacRuntime.optimal.stop;
    
    // Simulação de dados de economia (apenas em %)
    const savingsData = [
      { month: 'Jan', economia: 20.8 },
      { month: 'Fev', economia: 20.0 },
      { month: 'Mar', economia: 23.1 },
      { month: 'Abr', economia: 20.0 },
      { month: 'Mai', economia: 24.0 },
      { month: 'Jun', economia: 23.7 }
    ];

    const avgSavingsPercent = (savingsData.reduce((acc, cur) => acc + cur.economia, 0) / savingsData.length).toFixed(2);

    return (
      <div className="technical-view-container">
        {/* Header Premium */}
        <div className="technical-header-premium optimus-gradient">
          <div className="header-glow"></div>
          <div className="header-content-tech">
            <div className="header-left-tech">
              <motion.div 
                className="header-icon-tech"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <FiPlay size={40} />
              </motion.div>
    <div>
                <h1 className="header-title-tech">Optimum Start/Stop</h1>
                <p className="header-subtitle-tech">
                  Sugestão de horário otimizado baseado em análise de ocupação e conforto térmico
                </p>
            </div>
          </div>
        </div>
      </div>

        {/* Optimal Times Display */}
        <div className="optimal-times-container">
          <motion.div 
            className="time-card start-time"
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="time-icon">
              <FiPlay size={24} />
            </div>
            <div className="time-info">
              <span className="time-label">Horário Ideal de Início</span>
              <div className="time-value">{optimalStart}</div>
              <span className="time-description">Pré-condicionamento otimizado</span>
            </div>
            <div className="time-benefit">
              <FiTrendingUp size={16} />
              <span>-30min vs atual</span>
            </div>
          </motion.div>

          <div className="time-flow">
            <div className="flow-line"></div>
            <div className="flow-duration">
              <FiClock size={20} />
              <span>11h de operação</span>
            </div>
          </div>

          <motion.div 
            className="time-card stop-time"
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="time-icon">
              <FiPause size={24} />
            </div>
            <div className="time-info">
              <span className="time-label">Horário Ideal de Parada</span>
              <div className="time-value">{optimalStop}</div>
              <span className="time-description">Desligamento antecipado</span>
            </div>
            <div className="time-benefit">
              <FiTrendingDown size={16} />
              <span>-45min vs atual</span>
            </div>
          </motion.div>
        </div>

        {/* Savings Analysis */}
        <div className="savings-analysis">
          <div className="savings-header">
            <h3>Análise de Economia Projetada</h3>
            <div className="savings-summary">
              <div className="summary-item">
                <span className="summary-label">Redução Média</span>
                <span className="summary-value">{avgSavingsPercent}%</span>
              </div>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={savingsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
                formatter={(value) => `${value}%`}
              />
              <Legend />
              <Bar dataKey="economia" fill="#10b981" name="Economia (%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Implementation Benefits */}
        <div className="benefits-grid">
          <motion.div className="benefit-card" whileHover={{ y: -5 }}>
            <FiCheckCircle className="benefit-icon" />
            <h4>Conforto Mantido</h4>
            <p>Temperatura ideal garantida durante ocupação</p>
          </motion.div>
          <motion.div className="benefit-card" whileHover={{ y: -5 }}>
            <FiTrendingDown className="benefit-icon" />
            <h4>Redução de Custos</h4>
            <p>Economia estimada de 20-25% na conta de energia</p>
          </motion.div>
          <motion.div className="benefit-card" whileHover={{ y: -5 }}>
            <FiActivity className="benefit-icon" />
            <h4>Vida Útil Estendida</h4>
            <p>Menor desgaste do equipamento HVAC</p>
          </motion.div>
      </div>
    </div>
  );
  };

  // Componente para Setpoint Dinâmico
  const SetpointView = () => {
    const currentSetpoint = technical.setpoint.current;
    const recommendedSetpoint = technical.setpoint.recommended;
    const adjustment = technical.setpoint.adjustment;
    
    // Dados para simulação de conforto vs economia
    const comfortEconomyData = [
      { setpoint: 20, conforto: 60, economia: 95 },
      { setpoint: 21, conforto: 70, economia: 85 },
      { setpoint: 22, conforto: 85, economia: 75 },
      { setpoint: 23, conforto: 95, economia: 65 },
      { setpoint: 24, conforto: 90, economia: 55 },
      { setpoint: 25, conforto: 75, economia: 45 },
      { setpoint: 26, conforto: 60, economia: 35 }
    ];

    // Fatores que influenciam o setpoint
    const factors = [
      { name: 'Temperatura Externa', value: 32, impact: 'high', icon: FiSun },
      { name: 'Umidade Relativa', value: 65, impact: 'medium', icon: FiDroplet },
      { name: 'Ocupação', value: 75, impact: 'high', icon: FiActivity },
      { name: 'Radiação Solar', value: 'Alta', impact: 'medium', icon: FiSun }
    ];

    return (
      <div className="technical-view-container">
        {/* Header Premium */}
        <div className="technical-header-premium setpoint-gradient">
          <div className="header-glow"></div>
          <div className="header-content-tech">
            <div className="header-left-tech">
<div className="header-icon-tech">
  <motion.div
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    <motion.div
      animate={{ 
        rotate: [0, 45, 0, -45, 0],
        scale: [1, 1.1, 1, 1.1, 1]
      }}
      transition={{ 
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      <FiSliders size={40} />
    </motion.div>
  </motion.div>
            </div>
            <div>
                <h1 className="header-title-tech">Setpoint Dinâmico</h1>
                <p className="header-subtitle-tech">
                  Recomendação inteligente de temperatura otimizada para eficiência e conforto
                </p>
            </div>
          </div>
        </div>
      </div>

        {/* Setpoint Comparison */}
        <div className="setpoint-comparison-container">
          <motion.div 
            className="setpoint-display current"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
          >
            <div className="setpoint-header">
              <FiThermometer size={20} />
              <span>Setpoint Atual</span>
            </div>
            <div className="setpoint-temp">
              <SafeCountUp 
                value={currentSetpoint != null ? Math.round(currentSetpoint) : null}
                start={20}
                duration={2}
                decimals={0}
                suffix="°C"
                className="temp-value"
              />
            </div>
            <div className="setpoint-status">
              <span className="status-badge">Em uso</span>
            </div>
          </motion.div>

          <div className="setpoint-arrow-container">
            <motion.div 
              className="arrow-wrapper"
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="arrow-line"></div>
              <div className="arrow-head"></div>
              <div className="adjustment-badge">
                {adjustment > 0 ? '+' : ''}{Math.round(adjustment)}°C
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="setpoint-display recommended"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, type: "spring" }}
          >
            <div className="setpoint-header">
              <FiTarget size={20} />
              <span>Setpoint Recomendado</span>
            </div>
            <div className="setpoint-temp recommended">
              <SafeCountUp 
                value={recommendedSetpoint != null ? Math.round(recommendedSetpoint) : null}
                start={20}
                duration={2}
                decimals={0}
                suffix="°C"
                className="temp-value"
              />
            </div>
            <div className="setpoint-status">
              <span className="status-badge recommended">Otimizado</span>
            </div>
          </motion.div>
        </div>

        {/* Reason Box */}
        <div className="reason-container">
          <div className="reason-header">
            <FiAlertCircle size={20} />
            <h3>Motivo da Recomendação</h3>
          </div>
          <p className="reason-text">{technical.setpoint.reason}</p>
          <div className="reason-benefits">
            <div className="benefit-item">
              <FiCheckCircle size={16} />
              <span>Economia de energia estimada: 15%</span>
            </div>
            <div className="benefit-item">
              <FiCheckCircle size={16} />
              <span>Manutenção do conforto térmico</span>
        </div>
            <div className="benefit-item">
              <FiCheckCircle size={16} />
              <span>Redução de ciclos ON/OFF</span>
          </div>
        </div>
      </div>

        {/* Comfort vs Economy Chart */}
        <div className="chart-section-tech">
          <h3>Análise Conforto vs Economia</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={comfortEconomyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="setpoint" 
                stroke="#9ca3af"
                label={{ value: 'Setpoint (°C)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                stroke="#9ca3af"
                label={{ value: 'Percentual (%)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                contentStyle={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="conforto" 
                stroke="#3b82f6" 
                strokeWidth={3}
                dot={{ fill: '#3b82f6', r: 5 }}
                name="Conforto"
              />
              <Line 
                type="monotone" 
                dataKey="economia" 
                stroke="#10b981" 
                strokeWidth={3}
                dot={{ fill: '#10b981', r: 5 }}
                name="Economia"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Influencing Factors */}
        <div className="factors-section">
          <h3>Fatores de Influência</h3>
          <div className="factors-grid">
            {factors.map((factor, index) => {
              const IconComponent = factor.icon;
              return (
                <motion.div 
                  key={factor.name}
                  className={`factor-card impact-${factor.impact}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="factor-icon">
                    <IconComponent size={24} />
                  </div>
                  <div className="factor-info">
                    <h4>{factor.name}</h4>
                    <p className="factor-value">
                      {typeof factor.value === 'number' ? `${factor.value}%` : factor.value}
                    </p>
                    <div className="impact-indicator">
                      <span className="impact-label">Impacto:</span>
                      <div className="impact-bars">
                        <span className={`bar ${factor.impact === 'high' || factor.impact === 'medium' ? 'filled' : ''}`}></span>
                        <span className={`bar ${factor.impact === 'high' || factor.impact === 'medium' ? 'filled' : ''}`}></span>
                        <span className={`bar ${factor.impact === 'high' ? 'filled' : ''}`}></span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
      </div>
    </div>
  );
  };

  // Renderização baseada na subseção ativa
  const renderContent = () => {
  switch (activeSubsection) {
      case 'runtime':
        return <RuntimeView />;
    case 'optimus':
      return <OptimusView />;
    case 'setpoint':
      return <SetpointView />;
    default:
        return <RuntimeView />;
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeSubsection}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {renderContent()}
      </motion.div>
    </AnimatePresence>
  );
};

export default TechnicalData;
